import { useState, useEffect, useRef } from "react";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import CardActionArea from "@material-ui/core/CardActionArea";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import { useAuth } from "./../util/auth";
import { useRouter } from "./../util/router.js";
import { useItemsByOwner } from "./../util/db";
import { modifiedApiRequest } from "../util/util";
import { thumbUrl, displayUrl, imgFallback } from "../util/imageUrl";
import {
  Video as VideoIcon,
  Sparkles,
  Download,
  RefreshCw,
} from "lucide-react";

const ADMIN_UID = "ZzNrLpGG8MPjgNBKuPlpgFFjAbs2";
const POLL_INTERVAL_MS = 4000;
const MAX_POLL_ATTEMPTS = 180; // 12 minutes

function extractList(payload, key) {
  if (!payload) return [];
  const list = Array.isArray(payload) ? payload : payload[key];
  if (!Array.isArray(list)) return [];
  return list
    .map((m) => {
      if (typeof m === "string") return { id: m, name: m };
      if (m && typeof m === "object") {
        const id = m.id || m.name || m.value;
        const name = m.name || m.label || id;
        return id ? { id, name } : null;
      }
      return null;
    })
    .filter(Boolean);
}

function VideoGenerator(props) {
  const auth = useAuth();
  const router = useRouter();

  const [selectedImageUrl, setSelectedImageUrl] = useState(
    router.query.imageUrl || ""
  );
  const [model, setModel] = useState("");

  const [modelOptions, setModelOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [modelsError, setModelsError] = useState(null);

  const [pending, setPending] = useState(false);
  const [stage, setStage] = useState(null);
  const [queueNumber, setQueueNumber] = useState(null);
  const [queueTime, setQueueTime] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const pollTimerRef = useRef(null);

  const { data: galleryItems, status: galleryStatus } =
    useItemsByOwner(ADMIN_UID);
  const gallery = (galleryItems || []).filter((it) => !!it.url);
  const galleryLoading = galleryStatus === "loading";

  useEffect(() => {
    if (!auth?.user) return;
    let cancelled = false;
    setOptionsLoading(true);
    setModelsError(null);
    modifiedApiRequest("video-generations/models", "GET")
      .then((modelsData) => {
        if (cancelled) return;
        const models = extractList(modelsData, "models");
        if (models.length === 0)
          setModelsError(
            "Upstream returned no models. Check VIDEO_API_TOKEN on this deployment."
          );
        setModelOptions(models);
        if (models.length && !model) setModel(models[0].id);
        setOptionsLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setModelsError(e?.message || "Models request failed");
        setModelOptions([]);
        setOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user]);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  const canGenerate = !pending && !!selectedImageUrl && !!model;

  const pollStatus = (docId, attempt = 0) => {
    pollTimerRef.current = setTimeout(async () => {
      try {
        const data = await modifiedApiRequest(
          `video-generations/status?id=${encodeURIComponent(docId)}`,
          "GET"
        );
        if (typeof data.queueNumber === "number") setQueueNumber(data.queueNumber);
        if (data.stage) setStage(data.stage);
        if (data.status === "complete" && data.videoUrl) {
          setVideoUrl(data.videoUrl);
          setPending(false);
          return;
        }
        if (data.status === "failed") {
          setErrorMsg(data.error || "Video generation failed.");
          setPending(false);
          return;
        }
        if (attempt + 1 >= MAX_POLL_ATTEMPTS) {
          setErrorMsg(
            "Still rendering — this is taking longer than usual. Check back in a few minutes."
          );
          setPending(false);
          return;
        }
        pollStatus(docId, attempt + 1);
      } catch (err) {
        setErrorMsg(err.message || "Could not fetch status");
        setPending(false);
      }
    }, POLL_INTERVAL_MS);
  };

  const handleGenerate = async () => {
    if (!auth?.user) {
      router.push("/auth/signin");
      return;
    }
    if (!selectedImageUrl || !model) return;

    setErrorMsg(null);
    setVideoUrl(null);
    setQueueNumber(null);
    setQueueTime(null);
    setStage(null);
    setPending(true);

    try {
      const resp = await modifiedApiRequest(
        "video-generations/create",
        "POST",
        { imageUrl: selectedImageUrl, model }
      );
      if (!resp.docId) {
        throw new Error(resp.message || "Could not start generation");
      }
      setStage(resp.stage || "animate");
      if (typeof resp.queueNumber === "number") setQueueNumber(resp.queueNumber);
      if (typeof resp.queueTime === "number") setQueueTime(resp.queueTime);
      pollStatus(resp.docId);
    } catch (err) {
      setErrorMsg(err.message || "Could not start generation");
      setPending(false);
    }
  };

  const handleDownload = () => {
    if (videoUrl) window.open(videoUrl, "_blank");
  };

  const overlineStyle = {
    display: "block",
    marginBottom: 8,
    letterSpacing: 1,
    opacity: 0.7,
  };

  const stageLabel =
    stage === "animate" ? "Rendering your video…" : "Starting…";

  return (
    <Grid container spacing={4}>
      {/* LEFT: controls */}
      <Grid item xs={12} md={7}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="overline" style={overlineStyle}>
              Pick an image to animate
            </Typography>
            {galleryLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} />
              </Box>
            ) : gallery.length === 0 ? (
              <Box
                style={{
                  padding: 16,
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.03)",
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" style={{ opacity: 0.8 }}>
                  No gallery images available yet.
                </Typography>
              </Box>
            ) : (
              <Box
                style={{
                  maxHeight: 360,
                  overflowY: "auto",
                  paddingRight: 8,
                }}
              >
                <Grid container spacing={2}>
                  {gallery.map((item) => {
                    const selected = item.url === selectedImageUrl;
                    return (
                      <Grid item xs={6} sm={4} key={item.id || item.url}>
                        <Card
                          style={{
                            outline: selected
                              ? "2px solid rgba(244, 81, 30, 0.9)"
                              : "2px solid transparent",
                            transition: "outline-color 120ms ease",
                          }}
                        >
                          <CardActionArea
                            onClick={() => setSelectedImageUrl(item.url)}
                          >
                            {/* 130px picker thumbnails. selectedImageUrl keeps
                                item.url (the original), which is what gets
                                sent upstream for animation. */}
                            <img
                              src={thumbUrl(item.url)}
                              onError={imgFallback(item.url)}
                              loading="lazy"
                              decoding="async"
                              alt=""
                              style={{
                                width: "100%",
                                height: 130,
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <FormControl variant="outlined" fullWidth disabled={optionsLoading}>
              <InputLabel id="video-model-label">Animation model</InputLabel>
              <Select
                labelId="video-model-label"
                label="Animation model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
              >
                {modelOptions.length === 0 && (
                  <MenuItem value="" disabled>
                    {optionsLoading
                      ? "Loading…"
                      : modelsError || "No models available"}
                  </MenuItem>
                )}
                {modelOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleGenerate}
              disabled={!canGenerate}
              fullWidth
              startIcon={!pending && <Sparkles size={18} />}
              style={{
                paddingTop: 14,
                paddingBottom: 14,
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              {pending ? (
                <CircularProgress size={26} />
              ) : (
                <span>{props.buttonAction || "Generate video"}</span>
              )}
            </Button>
          </Grid>

          {errorMsg && (
            <Grid item xs={12}>
              <Box
                style={{
                  padding: "12px 14px",
                  borderRadius: 10,
                  border: "1px solid #d9534f",
                  color: "#d9534f",
                  background: "rgba(217, 83, 79, 0.12)",
                }}
              >
                <Typography variant="caption" style={{ fontSize: 12 }}>
                  {errorMsg}
                </Typography>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                borderRadius: 10,
                padding: "12px 14px",
                border: "1px solid #1595a9",
                color: "#117b8c",
                background: "rgba(23, 162, 184, 0.15)",
              }}
            >
              <Typography variant="caption" style={{ fontSize: 11 }}>
                Videos are 5 seconds long and cost 10 credits each —
                available to subscribed members only.
              </Typography>
              <Typography variant="caption" style={{ fontSize: 11 }}>
                Violence, gore, celebrity names, and minors are prohibited and
                will be removed.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      {/* RIGHT: sticky preview */}
      <Grid item xs={12} md={5}>
        <Box style={{ position: "sticky", top: 16 }}>
          <Box
            style={{
              width: "100%",
              position: "relative",
              paddingTop: "128.57%",
              borderRadius: 12,
              border: "2px dashed rgba(255,255,255,0.15)",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
              overflow: "hidden",
            }}
          >
            <Box
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {videoUrl ? (
                <video
                  src={videoUrl}
                  controls
                  autoPlay
                  loop
                  playsInline
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              ) : pending ? (
                <Box
                  style={{
                    textAlign: "center",
                    color: "rgba(255,255,255,0.85)",
                    padding: "0 24px",
                  }}
                >
                  <CircularProgress />
                  <Typography
                    variant="body2"
                    style={{ marginTop: 12, fontWeight: 500 }}
                  >
                    {stageLabel}
                  </Typography>
                  {(queueNumber !== null || queueTime !== null) && (
                    <Typography
                      variant="caption"
                      style={{ display: "block", marginTop: 6, opacity: 0.8 }}
                    >
                      {queueNumber !== null
                        ? `Queue position: ${queueNumber}`
                        : ""}
                      {queueNumber !== null && queueTime ? " · " : ""}
                      {queueTime ? `~${Math.ceil(queueTime)}s` : ""}
                    </Typography>
                  )}
                </Box>
              ) : selectedImageUrl ? (
                <>
                  {/* Preview only — selectedImageUrl (the original) is what
                      gets sent upstream for animation. */}
                  <img
                    src={displayUrl(selectedImageUrl)}
                    onError={imgFallback(selectedImageUrl)}
                    decoding="async"
                    alt="Selected"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <Box
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: "8px 12px",
                      textAlign: "center",
                      background:
                        "linear-gradient(0deg, rgba(0,0,0,0.65), rgba(0,0,0,0))",
                    }}
                  >
                    <Typography
                      variant="caption"
                      style={{ color: "rgba(255,255,255,0.95)" }}
                    >
                      Ready to animate · 5-second video · 10 credits
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box
                  style={{
                    textAlign: "center",
                    color: "rgba(255,255,255,0.5)",
                    padding: "0 24px",
                  }}
                >
                  <VideoIcon size={56} strokeWidth={1.25} />
                  <Typography
                    variant="body2"
                    style={{ marginTop: 12, fontWeight: 500 }}
                  >
                    Your video will appear here
                  </Typography>
                  <Typography
                    variant="caption"
                    style={{
                      display: "block",
                      marginTop: 6,
                      opacity: 0.75,
                    }}
                  >
                    Pick an image, choose a model, then hit Generate.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {videoUrl && (
            <Box mt={2} display="flex" style={{ gap: 12 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleDownload}
                startIcon={<Download size={16} />}
              >
                Open video
              </Button>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleGenerate}
                disabled={!canGenerate}
                startIcon={<RefreshCw size={16} />}
              >
                Regenerate
              </Button>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}

export default VideoGenerator;
